import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../lib/api";
import { errMsg } from "../../lib/portfolio";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent } from "../../components/ui/card";

const sel = "mt-1 block h-11 rounded-md border border-erf-borderCream bg-white px-3";

export function SchedulePanel({ pid, manager }) {
  const [s, setS] = useState(null);
  const [f, setF] = useState({ name: "", kind: "task", planned_start: "", planned_finish: "" });
  const [dep, setDep] = useState({ successor: "", predecessor_id: "", relation: "FS", lag_days: 0 });
  const [error, setError] = useState(null);
  const load = useCallback(() => api.get(`/phase3/schedule/projects/${pid}`).then((r) => setS(r.data)), [pid]);
  useEffect(() => { load(); }, [load]);
  const run = (p) => p.then(() => { setError(null); load(); }).catch((e) => setError(errMsg(e, "That did not work.")));
  if (!s) return null;
  const name = Object.fromEntries(s.activities.map((a) => [a.id, a.name]));
  const add = (e) => {
    e.preventDefault();
    const body = { ...f, planned_finish: f.kind === "milestone" ? f.planned_start : f.planned_finish };
    run(api.post(`/phase3/schedule/projects/${pid}/activities`, body).then(() => setF({ ...f, name: "" })));
  };
  return (
    <Card className="ds-card border-erf-borderCream mt-6" data-testid="phase3-schedule-panel">
      <CardContent className="p-5 space-y-3 text-sm font-nav text-erf-dark">
        <h2 className="font-display font-semibold text-base md:text-lg">Schedule</h2>
        <p className="text-xs text-erf-dark/80" data-testid="phase3-schedule-note">{s.note}</p>
        {manager && (
          <form className="flex flex-wrap items-end gap-3" onSubmit={add}>
            <label>Activity<Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="mt-1 h-11" data-testid="phase3-sched-name" /></label>
            <label>Type<select value={f.kind} onChange={(e) => setF({ ...f, kind: e.target.value })} className={sel} data-testid="phase3-sched-kind"><option value="task">Task</option><option value="milestone">Milestone</option></select></label>
            <label>Start<Input type="date" required value={f.planned_start} onChange={(e) => setF({ ...f, planned_start: e.target.value })} className="mt-1 h-11" data-testid="phase3-sched-start" /></label>
            {f.kind === "task" && <label>Finish<Input type="date" required value={f.planned_finish} onChange={(e) => setF({ ...f, planned_finish: e.target.value })} className="mt-1 h-11" data-testid="phase3-sched-finish" /></label>}
            <Button type="submit" className="h-11 bg-erf-dark text-[#FEFCF7]" data-testid="phase3-sched-add">Add</Button>
          </form>
        )}
        {manager && s.activities.length > 1 && (
          <form className="flex flex-wrap items-end gap-3" onSubmit={(e) => { e.preventDefault(); run(api.post(`/phase3/schedule/activities/${dep.successor}/dependencies`, { predecessor_id: dep.predecessor_id, relation: dep.relation, lag_days: Number(dep.lag_days) || 0 })); }}>
            <label>This activity<select required value={dep.successor} onChange={(e) => setDep({ ...dep, successor: e.target.value })} className={sel} data-testid="phase3-dep-successor"><option value="">Choose…</option>{s.activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
            <label>Depends on<select required value={dep.predecessor_id} onChange={(e) => setDep({ ...dep, predecessor_id: e.target.value })} className={sel} data-testid="phase3-dep-predecessor"><option value="">Choose…</option>{s.activities.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
            <label>Link<select value={dep.relation} onChange={(e) => setDep({ ...dep, relation: e.target.value })} className={sel} data-testid="phase3-dep-relation"><option value="FS">Finish → Start</option><option value="SS">Start → Start</option><option value="FF">Finish → Finish</option><option value="SF">Start → Finish</option></select></label>
            <label>Lag (days)<Input inputMode="numeric" value={dep.lag_days} onChange={(e) => setDep({ ...dep, lag_days: e.target.value })} className="mt-1 h-11 w-24" data-testid="phase3-dep-lag" /></label>
            <Button type="submit" variant="outline" className="h-11 border-erf-borderCream" data-testid="phase3-dep-add">Link</Button>
          </form>
        )}
        {error && <p role="alert" className="text-sm text-[#991B1B]" data-testid="phase3-sched-error">{error}</p>}
        <ul className="space-y-1">
          {s.activities.length === 0 && <li>No activities yet.</li>}
          {s.activities.map((a) => (
            <li key={a.id} data-testid={`phase3-sched-${a.id}`}>
              <strong>{a.name}</strong> {a.kind === "milestone" ? "◆" : ""} {a.planned_start}{a.kind === "task" ? ` → ${a.planned_finish}` : ""}
              {a.revision > 0 && ` · replanned ${a.revision}×`}{a.finish_variance_days != null && ` · finished ${a.finish_variance_days} day(s) vs plan`}
            </li>
          ))}
        </ul>
        {s.dependencies.length > 0 && (
          <ul className="space-y-1" data-testid="phase3-dep-list">
            {s.dependencies.map((d) => (
              <li key={d.id} className={d.violated ? "text-[#991B1B]" : ""} data-testid={`phase3-dep-${d.id}`}>
                {name[d.predecessor_id]} → {name[d.successor_id]} ({d.relation}{d.lag_days ? ` +${d.lag_days}d` : ""}){d.violated ? " — dates conflict" : ""}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
